/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import * as React from 'react';

import { Modal, ModalBody, ModalHeader, format } from '@msdyn365-commerce-modules/utilities';
import { IImageSettings, Image, RichTextComponent, msdyn365Commerce, getRichTextHtml } from '@msdyn365-commerce/core';

import { FinancingOfferStateInput } from '../../data-actions/financing-offer-state.action';
import {
    getValidSpecialCampaign,
    IFinancingOfferCampaign,
    IFinancingOfferFormatted
} from '../../Utilities/select-financing-offer';
import splitPrice from '../../Utilities/splitPrice';
import { IMfrmCcOfferModalData } from './mfrm-cc-offer-modal.data';
import { IMfrmCcOfferModalProps } from './mfrm-cc-offer-modal.props.autogenerated';
import ActionCard from './components/action-card';
import CTAButton from './components/CTA-Button';
import { callUtagLink } from '../../Utilities/analytics/utagCaller';

export interface IMfrmCcOfferModalViewProps extends IMfrmCcOfferModalProps<IMfrmCcOfferModalData> {
    content: React.ReactNode;
}

/**
 *
 * MfrmCcOfferModal component
 * @extends {React.PureComponent<IMfrmCcOfferModalProps<IMfrmCcOfferModalData>>}
 */

class MfrmCcOfferModal extends React.PureComponent<IMfrmCcOfferModalProps<IMfrmCcOfferModalData>> {
    private totalPrice: number = 0;
    private timer: NodeJS.Timeout | null;
    private moduleClassName: string = 'mfrm-financing-modal';

    constructor(props: IMfrmCcOfferModalProps<IMfrmCcOfferModalData>) {
        super(props);
        this.timer = null;
    }

    public componentWillUnmount(): void {
        if (this.timer) {
            clearTimeout(this.timer);
        }
    }

    public render(): JSX.Element | null {
        if (!this.props.data.financingOfferState.result?.financingOffer) {
            return null;
        } else {
            const { totalPrice, financingOffer } = this.props.data.financingOfferState.result;
            if (totalPrice) {
                this.totalPrice = totalPrice;
            }
            const viewProps: IMfrmCcOfferModalViewProps = {
                ...this.props,
                content: this._buildContent(financingOffer)
            };
            return this.props.renderView(viewProps);
        }
    }


    private onCallClick = () => {
        // @ts-ignore
        if ('utag' in window) {
            // @ts-ignore
            // tslint:disable-next-line: object-literal-key-quotes
            utag.link({
                event: 'call_engagement',
                event_type: 'call_engagement',
                event_category: 'call',
                event_action: 'synchrony modal click',
                event_label: 'call engagement',
                event_noninteraction: 'false'
            });
        }
    };

    private onCallKeyUp = (e: React.KeyboardEvent<HTMLAnchorElement>) => {
        if (e.keyCode === 13) {
            this.onCallClick;
        }
    };

    private onChatClick = (event: React.SyntheticEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        this.timer = setTimeout(function () {
            // eslint-disable-next-line no-eval, security/detect-eval-with-expression
            eval(
                `var elems = document.querySelectorAll('.uiButton.helpButtonEnabled, .minimizedContainer.helpButton, .sidebarHeader.minimizedContainer'); if(elems != undefined && elems.length > 0) { if(elems.length > 1){elems[1].click()} else {elems[0].click()}}`
            );
            // eslint-disable-next-line no-eval
            // eval(
            //     `document.querySelectorAll('.embeddedServiceHelpButton')?.[0]?.classList.add('show-chat');`
            // );
        }, 1000);

        // @ts-ignore
        if ('utag' in window) {
            // @ts-ignore
            // tslint:disable-next-line: object-literal-key-quotes
            utag.link({
                event: 'chat-widget-initiated',
                event_type: 'chat_click',
                event_category: 'chat',
                event_action: 'click',
                event_label: 'chat engagement'
            });
        }

        this._toggleModal();
    };

    private onChatKeyUp = (e: React.KeyboardEvent<HTMLAnchorElement>) => {
        if (e.keyCode === 13) {
            this.onChatClick;
        }
    };

    private applyNowClick = () => {
        /* VSI customization START - 61856 Tracking */
        const {
            context: {
                actionContext: {
                    requestContext: { url: requestUrl }
                }
            }
        } = this.props;
        if (requestUrl.requestUrl.href.includes('cart')) {
            void callUtagLink({
                event: 'Cart-Synchrony-Apply',
                event_category: 'Financing',
                event_action: 'Cart page Apply Now button click',
                event_label: 'Synchrony - Apply Now',
                event_noninteraction: 'false',
                event_type: 'synch_button'
            });
        } else if (requestUrl.requestUrl.href.includes('checkout')) {
            void callUtagLink({
                event: 'Payment-Synchrony-Apply',
                event_category: 'Financing',
                event_action: 'Payment page Apply Now button click',
                event_label: 'Synchrony - Apply Now',
                event_noninteraction: 'false',
                event_type: 'synch_button'
            });
        } else {
            void callUtagLink({
                event: 'PDP-Synchrony-Apply',
                event_category: 'Financing',
                event_action: 'Button',
                event_label: 'Synchrony - Apply Now',
                event_noninteraction: 'false',
                event_type: 'synch_button'
            });
        }
        /* VSI customization END - 61856 Tracking */
    };

    private prequalifyNowClick = () => {
        // @ts-ignore
        // tslint:disable-next-line: object-literal-key-quotes
        'utag' in window && utag.link({
            event: 'PDP-Synchrony-Prequalify',
            event_category: 'Financing',
            event_action: 'Button',
            event_label: 'Synchrony - Prequalify Now',
            event_noninteraction: 'false',
            event_type: 'synch_button'
        });
    };

    private applyNowKeyUp = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.keyCode === 13) {
            this.applyNowClick;
        }
    };

    private prequalifyNowKeyUp = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.keyCode === 13) {
            this.prequalifyNowClick;
        }
    };

    private _toggleModal = (): void => {
        if (this.props.data.financingOfferState.result) {
            this.props.context.actionContext.update(new FinancingOfferStateInput(), {
                isOpen: !this.props.data.financingOfferState.result.isOpen
            });
        }
    };

    private _focusModal = (): void => {
        if (msdyn365Commerce.isBrowser && this.props.data.financingOfferState.result?.isOpen) {
            document.getElementById('financingModal')?.focus();
        }
    };
    private _getLogo(): React.ReactNode {
        const img = this.props.config.logoImage;

        const defaultImageSettings: IImageSettings = {
            viewports: {
                xs: { q: 'w=238&h=85&m=8', w: 0, h: 0 },
                sm: { q: 'w=238&h=85&m=8', w: 0, h: 0 },
                md: { q: 'w=322&h=115&m=8', w: 0, h: 0 },
                lg: { q: 'w=322&h=115&m=8', w: 0, h: 0 }
            },
            lazyload: true
        };

        return (
            <div className={`${this.moduleClassName}__logo-container`}>
                <div className={`${this.moduleClassName}__logo`}>
                    <Image
                        {...img}
                        alt={img?.altText}
                        src={img?.src || ''}
                        gridSettings={this.props.context.request.gridSettings!}
                        imageSettings={(img && img.imageSettings) || defaultImageSettings}
                        role='img'
                    />
                </div>
            </div>
        );
    }

    private _getHeading(): React.ReactNode {
        const { header, subheader } = this.props.config;

        return (
            <div className={`${this.moduleClassName}__header-wrapper`}>
                <div className='h2' tabIndex={0}><span tabIndex={-1}>{header}</span></div>
                <p className={`${this.moduleClassName}__subheader`} tabIndex={0}><span tabIndex={-1}>{subheader}</span></p>
            </div>
        );
    }

    private _getCashBackBanner(financingOffer: IFinancingOfferFormatted): React.ReactNode {
        const { cashbackBannerDetails } = financingOffer;
        const { subTitleText, cashBackPercentageHeader } = this.props.config;
        return (

            <div className='mfrm-financing-modal__cashback-banner-wrapper'>
                <div className='mfrm-financing-modal__red-bar'>
                </div>
                <div className='mfrm-financing-modal__cashback-banner-content'>
                    <div className='mfrm-financing-modal__cashback-banner-text'>
                        <h1 className='cashback-heading'>{cashBackPercentageHeader}</h1>
                        <p className='cashback-subheading' dangerouslySetInnerHTML={getRichTextHtml(subTitleText)} />
                        <p className='cashback-text'>
                            <RichTextComponent text={cashbackBannerDetails} />
                        </p>
                    </div>
                    <div className='mfrm-financing-modal__cashback-banner-image'>
                        <img className='banner-image' src="https://images-us-prod.cms.dynamics365commerce.ms/cms/api/fknscsckgq/imageFileData/MAj8gp?pubver=1" alt="" />
                    </div>
                </div>
            </div>
        );

    }

    private _getStarted(): React.ReactNode {
        const { resources,
            config: { applyLink, phoneNo }
        } = this.props;

        const applyLinkData = applyLink && {
            linkText: applyLink.linkText || 'Link',
            linkUrl: applyLink.linkUrl?.destinationUrl,
            ariaLabel: applyLink.ariaLabel,
            openInNewTab: applyLink.openInNewTab
        };

        return (
            <div className={`${this.moduleClassName}__action-container`}>
                <div className={`${this.moduleClassName}__action-header`} tabIndex={0}>
                    <span tabIndex={-1}>{resources.ccModalGetStartedText}</span>
                </div>
                <div className={`${this.moduleClassName}__action-cards`}>
                    <ActionCard
                        href={applyLinkData.linkUrl}
                        target={applyLinkData.openInNewTab ? '_blank' : '_self'}
                        heading={resources.ccModalApplyText}
                        description={resources.ccModalApplyDescText}
                        showIcon={true}
                        iconClassName='icon-checkmark-outline'
                    />
                    <ActionCard
                        href={`tel:${phoneNo}`}
                        heading={resources.ccModalCallText}
                        description={format(resources.ccModalCallDescText, phoneNo)}
                        showIcon={true}
                        iconClassName='icon-phone'
                        onClick={() => this.onCallClick}
                        onKeyUp={() => this.onCallKeyUp}
                    />
                    <a
                        href=''
                        tabIndex={0}
                        className='mfrm-action-card'
                        onClick={this.onChatClick}
                        onKeyUp={this.onChatKeyUp}
                    >
                        <div className={`mfrm-action-card__wrapper`} tabIndex={-1}>
                            <i className='mfrm-action-card__icon icon-chat-outline' aria-hidden='true' tabIndex={-1} aria-label='icon' />
                            <span className='mfrm-action-card__heading'>{resources.ccModalChatText}</span>
                            <p className='mfrm-action-card__description'>{resources.ccModalChatDescText}</p>
                            <i className='mfrm-action-card__icon-chevron' aria-hidden='true' tabIndex={-1} aria-label='icon chevron' />
                        </div>
                    </a>
                </div>
            </div>
        );
    }

    private _getDisclaimer(financingOffer: IFinancingOfferFormatted): React.ReactNode {
        const disclaimerText = financingOffer.disclaimer;

        return (
            <div className='mfrm-financing-modal__disclaimer'>
                <RichTextComponent text={disclaimerText} />
            </div>
        );
    }

    private _getApplyNow(): React.ReactNode {
        const { applyLink, applyNowLabelText } = this.props.config;

        const applyLinkData = applyLink && {
            linkText: applyLink.linkText || 'Link',
            linkUrl: applyLink.linkUrl?.destinationUrl,
            ariaLabel: applyLink.ariaLabel,
            openInNewTab: applyLink.openInNewTab
        };

        return (
            <CTAButton
                linkData={applyLinkData}
                actionClick={this.applyNowClick}
                actionKeyUp={this.applyNowKeyUp}
                labelText={applyNowLabelText ?? ''}
            />
        );
    }

    private _getConsultPrequalification(): React.ReactNode {
        const { preQualifyLabelText } = this.props.config;
        const prequalifyLinkData = {
            linkText: 'See If you Prequalify',
            linkUrl: 'https://etail.mysynchrony.com/eapply/eapply.action?uniqueId=82FDD7321D7D37B51DBBE6B139A6876039013F724108EA60&preQual=Y&sitecode=mfadib5d1',
            ariaLabel: 'Prequalify Link',
            openInNewTab: true
        };

        return (
            <CTAButton
                linkData={prequalifyLinkData}
                actionClick={this.prequalifyNowClick}
                actionKeyUp={this.prequalifyNowKeyUp}
                labelText={preQualifyLabelText ?? ''}
            />
        );
    }
    private _getFinancingDetails(financingOffer: IFinancingOfferFormatted): React.ReactNode {
        const { costPerMonth, numOfMonths, isCashBack, isLTO, startDate, endDate, specialCampaign, everydayFinancingOfferMessage, limitedFinancingOfferMessage, lowCostFinancingOfferMessage, cashbackFinancingOfferMessage } = financingOffer;
        const { promotionalPeriodText, promotionalPurchaseAmountText } = this.props.config;
        const { resources, context } = this.props;
        const { specialFinancingEnable } = this.props.app.config;
        const isCampaignEnable = specialCampaign?.enableCampaign;
        const showEverydayFlag = specialCampaign?.showEverydayFlag;
        const everydayFinancingMessage = everydayFinancingOfferMessage && everydayFinancingOfferMessage?.replace('{numOfMonths}', numOfMonths.toString());
        const limitedFinancingrMessage = limitedFinancingOfferMessage && limitedFinancingOfferMessage?.replace('{numOfMonths}', numOfMonths.toString());
        const cashbackFinancingrMessage = cashbackFinancingOfferMessage && cashbackFinancingOfferMessage?.replace('{numOfMonths}', numOfMonths.toString());
        // if LTO use dates otherwise use 'Everyday'
        const APR = '0%';
        const promotionalPeriod = numOfMonths;
        const LTOeverydayOfferFlag = (promotionalPeriod === 36 || promotionalPeriod === 48 || promotionalPeriod === 60) ? 'Everyday' : `${startDate} - ${endDate}`;
        const offerValidFlag = specialFinancingEnable && this.props.data.specialSynchrony.result?.triggerSpecialFinancing ? `${startDate} - ${endDate}`
            : isCashBack ? `${startDate} - ${endDate}` : isLTO && isCampaignEnable && showEverydayFlag ? 'Everyday'
                : isLTO && isCampaignEnable && !showEverydayFlag ? `${startDate} - ${endDate}`
                    : isLTO ? LTOeverydayOfferFlag : 'Everyday';

        const financingDescription = financingOffer.financingOfferDescription;
        // Calculate the promo fee rounded down to the nearest cent
        const promoFee = (this.totalPrice * 0.02);
        let roundedPromoFee = Math.floor(promoFee * 100) / 100;
        const totalAmountFinanced = financingOffer?.isLTO ? parseFloat((Number(this.totalPrice) + roundedPromoFee)?.toFixed(2)) : this.totalPrice;


        // commented as per story # 20857 const { dollars: minPurchaseDollars, cents: minPurchaseCents } = splitPrice(minPurchase);
        const { dollars: totalPriceDollars, cents: totalPriceCents } = splitPrice(totalAmountFinanced);

        const identifier = (isLTO && isCampaignEnable) ? '*' : isLTO ? '**' : '***';

        const numberOfMonths = financingOffer?.numOfMonths === 36 ? financingOffer?.numOfMonths - 3 : financingOffer?.numOfMonths;

        // special campaign financing offer
        const campaign: IFinancingOfferCampaign | undefined = getValidSpecialCampaign(financingOffer);
        const campaignRemainingCost = campaign && financingOffer?.costPerMonth;

        return (
            <>
                <div className='mfrm-financing-modal__details-container'>
                    <div className='mfrm-financing-modal__dates-container' tabIndex={0}>
                        {financingOffer.offerName?.toLowerCase() === 'low cost offer' ? <div className='mfrm-financing-modal__valid-dates' tabIndex={-1}>Low Cost Offer</div> : <div className='mfrm-financing-modal__valid-dates' tabIndex={-1}>{offerValidFlag}</div>}
                    </div>

                    {campaign && campaignRemainingCost ? (
                        <>
                            <div className='mfrm-financing-modal__details-top'>
                                <div className='sr-only' tabIndex={0}>{`${context.cultureFormatter?.formatCurrency(Math.ceil(campaign.initialMonthlyAmount))} Per Month for the first ${campaign.numOfDiscountedMonths} months`}</div>
                                <div className='h3' aria-hidden='true'>
                                    {`$${Math.ceil(campaign.initialMonthlyAmount)}`}
                                    <span>/mo</span> for the first {campaign.numOfDiscountedMonths} months
                                </div>
                            </div>
                            <div className='mfrm-financing-modal__payments'>
                                <div className='sr-only' tabIndex={0}>{`then ${context.cultureFormatter?.formatCurrency(Math.ceil(campaignRemainingCost))} Per Month equal montly payments for ${numberOfMonths} months.`}</div>
                                <div aria-hidden='true'>
                                    then <span className='campaign-monthly-cost'>{`$${Math.ceil(campaignRemainingCost)}`}<span>/mo</span></span> equal
                                    monthly payments for <span className='campaign-months'>{numberOfMonths} months</span>
                                </div>
                            </div>
                        </>
                    ) : financingOffer.offerName?.toLowerCase() === 'low cost offer' ?
                        <>
                            <div className='mfrm-financing-modal__payments' tabIndex={0}>
                                <h3>{lowCostFinancingOfferMessage}</h3>
                            </div>
                        </> : (
                            <>
                                <div className='mfrm-financing-modal__details-top'>
                                    <div className='sr-only' tabIndex={0}>{`${context.cultureFormatter?.formatCurrency(costPerMonth)} Per Month`}</div>
                                    <div aria-hidden='true' className='h3'>
                                        {`$${costPerMonth}`}
                                        <span className='mfrm-financing-modal__per-month'>/MO</span>
                                    </div>
                                </div>
                                <div className='mfrm-financing-modal__payments' tabIndex={0}>
                                    {isLTO ? <span tabIndex={-1}>{limitedFinancingrMessage}</span> : isCashBack ? <span tabIndex={-1}>{cashbackFinancingrMessage}</span> : <span tabIndex={-1}>{everydayFinancingMessage}</span>}
                                </div>
                            </>
                        )}

                    {/* APR - PROMOTIONAL PERIOD - MIN PURCHASE - TOTAL PRICE */}
                    {financingOffer.offerName?.toLowerCase() !== 'low cost offer' &&
                        <>
                            <div className='mfrm-financing-modal__offer-details'>
                                {isLTO && (
                                    <div className='mfrm-financing-modal__detail'>
                                        <div className='sr-only' tabIndex={0}>{`APR ${APR}`}</div>
                                        <div aria-hidden='true'>
                                            <span className='mfrm-financing-modal__detail-heading'>APR</span>
                                            <span className='mfrm-financing-modal__sub-text'>{APR}</span>
                                        </div>
                                    </div>
                                )}
                                <div className='mfrm-financing-modal__detail'>
                                    <div className='sr-only' tabIndex={0}>{`${promotionalPeriodText ? promotionalPeriodText : resources.promoPeriodText}, ${promotionalPeriod} months${identifier}`}</div>
                                    <div aria-hidden='true'>
                                        <span className='mfrm-financing-modal__detail-heading'>{promotionalPeriodText ? promotionalPeriodText : resources.promoPeriodText}</span>
                                        <span className='mfrm-financing-modal__sub-text'>{promotionalPeriod} months<sup>{identifier}</sup></span>
                                    </div>
                                </div>
                                <div className='mfrm-financing-modal__detail'>
                                    <div className='sr-only' tabIndex={0}>{`${promotionalPurchaseAmountText ? promotionalPurchaseAmountText : resources.promoPurchaseAmountText}, ${context.cultureFormatter?.formatCurrency(this.totalPrice)}`}</div>
                                    <div aria-hidden='true'>
                                        <span className='mfrm-financing-modal__detail-heading'>{financingOffer?.isLTO ? resources.totalCostPurchaseAmountText : promotionalPurchaseAmountText ? promotionalPurchaseAmountText : resources.promoPurchaseAmountText}</span>
                                        <span className='mfrm-financing-modal__sub-text'>{`$${totalPriceDollars}${totalPriceCents ? `.${totalPriceCents}` : ''}`}</span>
                                    </div>
                                </div>
                            </div>
                        </>
                    }
                </div>
                {/* Financing Offer Description */}
                {financingDescription && (
                    <div className='mfrm-financing-modal__offer-container' tabIndex={0}>
                        <div tabIndex={-1}><RichTextComponent className='mfrm-financing-modal__offer-description' text={financingDescription} /></div>
                    </div>
                )}
            </>
        );
    }

    private _buildContent(financingOffer: IFinancingOfferFormatted): React.ReactNode {

        return (
            <Modal
                id={'financingModal'}
                autoFocus={true}
                fade={false}
                isOpen={this.props.data.financingOfferState.result?.isOpen}
                horizontalPosition={'center'}
                verticalPosition={'center'}
                zIndex={10011}
                toggle={this._toggleModal}
                applicationNode={'rsg-root'}
                className='mfrm-financing-modal'
                onLoad={this._focusModal}
            >
                <ModalHeader toggle={this._toggleModal} className='mfrm-financing-modal__header'>
                    {this._getLogo()}
                </ModalHeader>
                <ModalBody>
                    <>
                        <div className='mfrm-financing-modal__content'>
                            {this._getHeading()}
                            {financingOffer?.isCashBack && this._getCashBackBanner(financingOffer)}
                            {this._getFinancingDetails(financingOffer)}
                            {this._getStarted()}
                            {this._getDisclaimer(financingOffer)}
                        </div>
                        <div className='mfrm-financing-modal__button-group'>
                            {this._getConsultPrequalification()}
                            {this._getApplyNow()}
                        </div>
                    </>
                </ModalBody>
            </Modal>
        );
    }
}

export default MfrmCcOfferModal;
